import type { KeyboardEvent } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import type { ProjectTint } from './types';

const TINTS: ProjectTint[] = ['sand', 'green', 'blue', 'plain'];

/** The same name gets the same default colour until the person picks one. */
export function nameTint(name: string): ProjectTint {
  let sum = 0;
  for (const char of name.trim()) sum = (sum * 31 + char.codePointAt(0)!) % 9973;
  return TINTS[sum % TINTS.length];
}

export function ProjectTintPicker({
  value,
  onChange,
  disabled,
}: {
  value: ProjectTint;
  onChange: (tint: ProjectTint) => void;
  disabled: boolean;
}) {
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const labels = { sand: copy.sand, green: copy.green, blue: copy.blue, plain: copy.grey };
  const step: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
  const key = (event: KeyboardEvent<HTMLDivElement>) => {
    const by = step[event.key];
    if (!by || disabled) return;
    event.preventDefault();
    const index = TINTS.findIndex((option) => option === value);
    const next = TINTS[(index + by + TINTS.length) % TINTS.length];
    onChange(next);
    event.currentTarget.querySelector<HTMLElement>(`[data-tint="${next}"]`)?.focus();
  };
  return (
    <div
      className="ws-new-project-tints"
      role="radiogroup"
      aria-label={copy.colour}
      onKeyDown={key}
    >
      <span>{copy.colour}</span>
      {TINTS.map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          aria-label={labels[option]}
          data-tint={option}
          className="ws-new-project-tint"
          tabIndex={value === option ? 0 : -1}
          disabled={disabled}
          onClick={() => onChange(option)}
        />
      ))}
    </div>
  );
}
